import { ApolloError } from '@apollo/client'

import { useLazyPreviouslyPurchased } from '../../graphQL/queries/previouslyPurchased'
import {
  IPreviouslyPurchasedResponse,
  IPreviouslyPurchasedVariables,
} from '../../graphQL/queries/previouslyPurchased/types'
import {
  setPreviouslyPurchasedItems,
  setPreviouslyPurchasedStatus,
} from '../../redux/slices/recommendations'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import {
  handleRecommendationFetchError,
  mergeCartQuantitiesIntoRecommendationItems,
  skipIfMissingIdentifiers,
} from './recommendationHookHelpers'

const FUNCTION_NAME = 'usePreviouslyPurchased'

/**
 * Hook that fetches the `previouslyPurchased` recommendations carousel data
 * and writes it into the matching Redux slice. Triggered from the
 * EventManager via the `templateActions.previouslyPurchased.fetchProducts`
 * template action.
 */
export const usePreviouslyPurchased = () => {
  const dispatch = useAppDispatch()
  const config = useAppSelector((state) => state.defaultSlice.config)
  const cartItems = useAppSelector((state) => state.cartSlice.items)
  const cartGroups = useAppSelector((state) => state.cartSlice.groups)
  const cartActivePromotions = useAppSelector(
    (state) => state.cartSlice.activePromotions,
  )

  const onCompleted = ({
    previouslyPurchased,
  }: IPreviouslyPurchasedResponse) => {
    // Apollo invokes onCompleted on a detached microtask whose throws are
    // silently swallowed in production builds. Wrap the merge + dispatch so
    // any throw surfaces as ERROR + Datadog instead of leaving the slice
    // permanently in LOADING.
    try {
      const merged = mergeCartQuantitiesIntoRecommendationItems(
        previouslyPurchased.items,
        {
          items: cartItems,
          groups: cartGroups ?? [],
          activePromotions: cartActivePromotions,
        },
        config,
      )
      dispatch(setPreviouslyPurchasedItems(merged))
      dispatch(setPreviouslyPurchasedStatus('OK'))
    } catch (exception) {
      dispatch(setPreviouslyPurchasedStatus('ERROR'))
      handleRecommendationFetchError(
        exception,
        FUNCTION_NAME,
        'previouslyPurchased onCompleted threw during cart-quantity merge',
      )
    }
  }

  const onError = (error: ApolloError) => {
    dispatch(setPreviouslyPurchasedStatus('ERROR'))
    handleRecommendationFetchError(
      error,
      FUNCTION_NAME,
      'previouslyPurchased fetch failed',
    )
  }

  const [getPreviouslyPurchased] = useLazyPreviouslyPurchased({
    onCompleted,
    onError,
  })

  const fetchPreviouslyPurchased = async (
    variables: IPreviouslyPurchasedVariables,
  ) => {
    if (skipIfMissingIdentifiers(variables, FUNCTION_NAME, 'previouslyPurchased')) return
    dispatch(setPreviouslyPurchasedStatus('LOADING'))
    try {
      await getPreviouslyPurchased({ variables })
    } catch (exception) {
      dispatch(setPreviouslyPurchasedStatus('ERROR'))
      handleRecommendationFetchError(
        exception,
        FUNCTION_NAME,
        'previouslyPurchased fetch threw synchronously',
      )
    }
  }

  return fetchPreviouslyPurchased
}
